import { useInitialConfig } from '../utils/useInitialConfig';
import React, { useState, useEffect } from 'react';
import { buildLongitudeMarkers } from '../utils/longitudeMarkers';
import { buildLongitudeStudyReport } from '../utils/longitudeStudyReport';
import PropTypes from 'prop-types';
import { 
  Dialog, DialogTitle, DialogContent, DialogActions, Button, Select, MenuItem, 
  Typography, Box, TextField, RadioGroup, FormControlLabel, Radio, FormControl
} from '@mui/material';
import HelpIcon from './HelpIcon';
import { toYMD } from './DateUtils';
// Lives in utils/ so it can be tested without mounting the auth stack this dialog needs.
import { frameAbbrev } from '../utils/longitudeLabel';
import { logActivity } from '../utils/logActivity';
import { useAuth } from '../context/AuthContext';

const planets = ['Sun', 'Moon', 'Mercury', 'Venus', 'Mars', 'Jupiter', 'Saturn', 'Uranus', 'Neptune', 'Pluto', 'Rahu', 'Ketu'];
const COLORS = ['Red', 'Green', 'Blue', 'Yellow', 'Orange', 'Purple', 'Black', 'Gray', 'Brown', 'Pink', 'Cyan', 'White'];

/**
 * How each event is drawn. A vertical line marks the date across the whole pane; dots and arrows
 * sit against the bar itself, which stays readable when events are dense.
 */
/**
 * Bodies with no heliocentric position.
 *
 * The Sun is the origin of that frame. Rahu and Ketu are the lunar nodes — intersections of the
 * Moon's orbit around EARTH with the ecliptic — so they are undefined once Earth is not the
 * centre, and Swiss Ephemeris fails outright rather than returning a poor answer. Requesting one
 * used to surface as an HTTP 500.
 *
 * The Moon IS kept: its heliocentric longitude tracks Earth's, which makes it the usable proxy
 * for Earth in this frame, and Moon-planet pairs are the point of the study.
 */
const NO_HELIOCENTRIC_POSITION = ['Sun', 'Rahu', 'Ketu', 'North Node', 'South Node'];

/**
 * The two questions this dialog can ask.
 *
 * DISTANCE is the study that has always been here: where is the SEPARATION between two bodies
 * equal to an angle. TOTAL is where their longitudes SUM to one — the Vedic Yog reading, whose
 * boundaries fall every 13.333 degrees of Sun + Moon.
 *
 * They are not variations on each other. A separation is the same number in every zodiac, because
 * the ayanamsha cancels between the two terms; in a sum it doubles, so the frame decides the
 * answer. That is why Total has its own endpoint rather than a flag on the existing one.
 */
const MODE_DISTANCE = 'distance';
const MODE_TOTAL = 'total';

/**
 * The two blocks, described once and rendered by a loop.
 *
 * Written as data rather than as two hand-copied JSX blocks so the pair cannot drift apart: the
 * indent, the field width, the disabled rule and the reserved helper line are applied from one
 * place, which is what makes them read as two instances of the same choice rather than as two
 * controls that happen to sit near each other.
 */
const MODE_OPTIONS = [
  {
    value: MODE_DISTANCE,
    label: 'Distance (in degrees)',
    inputMode: 'numeric',
    hint: 'Angular separation between the two bodies, 0–359°.',
  },
  {
    value: MODE_TOTAL,
    label: 'Total',
    inputMode: 'decimal',
    hint: 'Sum of both longitudes, mod 360. A Yog boundary falls every 13.333°.',
  },
];

const DISPLAY_TYPES = [
  { value: 'verticalLine', label: 'Vertical Line' },
  { value: 'dot', label: 'Dot' },
  { value: 'arrow', label: 'Arrow' },
];

/**
 * Line styles for the vertical-line display.
 *
 * The values are ECharts' own `lineStyle.type` tokens, passed straight through as `lineType` on
 * the overlay — `chartDataProcessor` has honoured that field since projected Fibonacci levels
 * needed to draw dashed, so nothing in the rendering path had to change to support this.
 */
const LINE_STYLES = [
  { value: 'solid', label: 'Solid' },
  { value: 'dashed', label: 'Dashed' },
  { value: 'dotted', label: 'Dotted' },
];

// Module scope, not inside the component: it is a constant, and rebuilding it every render made
// it a new object each time, which is why the effect that reads it could not list it as a
// dependency without re-running on every render.
const PLANET2_COLOR_MAP = {
  'Sun': 'Orange',
  'Moon': 'Red',
  'Mercury': 'Gray',
  'Venus': 'Green',
  'Mars': 'Pink',
  'Jupiter': 'Blue',
  'Saturn': 'Brown',
  'Uranus': 'Cyan',
  'Neptune': 'Purple',
  'Pluto': 'Black',
  'Rahu': 'Teal',
  // Ketu is the point opposite Rahu, so it is offered wherever Rahu is. Yellow is unused by any
  // other body here and is already Ketu's colour in the declination dialog.
  'Ketu': 'Yellow',
};

/**
 * The prefill fields this dialog accepts, exported beside the applier that reads them.
 *
 * <p>⚠ `orb` is deliberately ABSENT. `/api/followstudy/get-degree-separation` takes no tolerance
 * -- it locates an EXACT crossing by sign change and bisection -- so a field for one would be
 * accepted, stored, and change nothing, which is the silent-ignore the prefill guard exists to
 * remove. `App.openDialog` refuses an unknown field and names this list, so a Copilot asking for
 * an orb is told why rather than quietly given a study it did not describe.
 */
export const PREFILL_FIELDS = Object.freeze([
  'planet1', 'planet2', 'distance', 'frameOfReference', 'color', 'displayType', 'lineType',
  'mode',
]);

export default function LongitudeStudyDlg({
  open, onClose, onRun, barData, onJournalAppend, initialConfig,
}) {
  const [lineType, setLineType] = useState('solid');
  const [displayType, setDisplayType] = useState('verticalLine');
  const { user, api } = useAuth();
  // Common state
  const [color, setColor] = useState(PLANET2_COLOR_MAP['Venus'] || COLORS[0]);
  const [frameOfReference, setFrameOfReference] = useState('Geocentric');
  const [planet1, setPlanet1] = useState('Sun');
  const [planet2, setPlanet2] = useState('Venus');
  const [distance, setDistance] = useState('0');

  // Distance by default: it is the study that already existed, and every saved habit assumes it.
  const [mode, setMode] = useState(MODE_DISTANCE);

  /**
   * PREFILL, applied once per distinct config object. Every field merges independently, so a
   * command naming only the two bodies leaves the colour and display type as the reader left them.
   *
   * <p>Declared BELOW `mode`, which it writes. A closure body runs after render so the binding
   * would resolve either way, but `App.tdz.test.js` exists because this file has paid for that
   * reasoning being wrong three times -- the readable order is the one that cannot be wrong.
   */
  useInitialConfig(initialConfig, (cfg) => {
    if (cfg.planet1 !== undefined) setPlanet1(cfg.planet1);
    if (cfg.planet2 !== undefined) setPlanet2(cfg.planet2);
    if (cfg.distance !== undefined) setDistance(String(cfg.distance));
    if (cfg.frameOfReference !== undefined) setFrameOfReference(cfg.frameOfReference);
    if (cfg.color !== undefined) setColor(cfg.color);
    if (cfg.displayType !== undefined) setDisplayType(cfg.displayType);
    if (cfg.lineType !== undefined) setLineType(cfg.lineType);
    if (cfg.mode !== undefined) setMode(cfg.mode);
  });
  // 13.333 is the first Yog boundary — the worked example in the brief — so it is the default
  // rather than 0, which is a legitimate target but tells a first-time reader nothing.
  const [total, setTotal] = useState('13.333');

  const [showLimitModal, setShowLimitModal] = useState(false);
  const [limitMessage, setLimitMessage] = useState("");

  // --- Helper for disabling logic ---
  const isRunDisabled = (() => {
    // These are MAXIMUM ELONGATIONS: Mercury never appears more than ~28 degrees from the Sun, so
    // a Distance study beyond that can never fire. They are a property of the SEPARATION and say
    // nothing about a SUM, which ranges over the whole circle for any pair — so Total is not
    // subject to them. Leaving the check in place for both modes would have made the study's own
    // worked example, Sun + Moon, unreachable for most targets.
    if (mode !== MODE_DISTANCE) {
      return false;
    }
    const d = parseInt(distance, 10);
    if (((planet1 === "Sun" && planet2 === "Mercury") || (planet1 === "Mercury" && planet2 === "Sun")) && d > 28) {
      return true;
    }
    if (((planet1 === "Sun" && planet2 === "Venus") || (planet1 === "Venus" && planet2 === "Sun")) && d > 48) {
      return true;
    }
    return false;
  })();

  useEffect(() => {
    if (isRunDisabled) {
      if ((planet1 === "Sun" && planet2 === "Mercury") || (planet1 === "Mercury" && planet2 === "Sun")) {
        setLimitMessage("For Sun and Mercury, the maximum allowed distance is 28 degrees.");
        setShowLimitModal(true);
      } else if ((planet1 === "Sun" && planet2 === "Venus") || (planet1 === "Venus" && planet2 === "Sun")) {
        setLimitMessage("For Sun and Venus, the maximum allowed distance is 48 degrees.");
        setShowLimitModal(true);
      }
    } else {
      setLimitMessage("");
      setShowLimitModal(false);
    }
  }, [isRunDisabled, planet1, planet2, distance]);

  const planetsAreIdentical = planet1 === planet2;

// Auto-update color based on planet2
useEffect(() => {
  if (PLANET2_COLOR_MAP[planet2]) {
    setColor(PLANET2_COLOR_MAP[planet2]);
  }
}, [planet2]);




  useEffect(() => {
    if (frameOfReference === 'Heliocentric') {
      const valid = planets.filter(p => !NO_HELIOCENTRIC_POSITION.includes(p));
      // Reset to Mercury rather than the list head, which is the Sun's old slot and reads as an
      // arbitrary jump; Mercury is the first body that actually has a heliocentric position.
      const fallback = valid.includes('Mercury') ? 'Mercury' : (valid[0] || '');
      if (NO_HELIOCENTRIC_POSITION.includes(planet1)) setPlanet1(fallback);
      if (NO_HELIOCENTRIC_POSITION.includes(planet2)) setPlanet2(fallback);
    }
  }, [frameOfReference, planet1, planet2]);

  const handleRun = async () => {
    const angle = parseInt(distance, 10);

    const eventDetails = {
      studyType: 'longitudinalDistance',
      parameters: {
        planet1,
        planet2,
        degrees: angle,
        frameOfReference,
      }
    };
    await logActivity(api, user, 'RUN_STUDY', eventDetails);
    if (!planet1 || !planet2 || isNaN(angle)) {
      console.error('[LongitudeStudyDlg] Missing required user selections:', { planet1, planet2, angle });
      alert('Please select both planets and enter a valid angle.');
      return;
    }
    if (planet1 === planet2) {
      console.error('[LongitudeStudyDlg] Identical planets selected:', { planet1, planet2 });
      alert('Planets cannot be identical for this study type.');
      return;
    }
    if (!barData || !Array.isArray(barData.dates) || barData.dates.length === 0) {
      console.error('[LongitudeStudyDlg] Chart data check failed:', barData);
      // Only show alert if truly empty, otherwise allow overlay rendering to proceed
      return;
    }
    // Ensure dates are in yyyy-MM-dd format

    // Build query params for getDegreeSeparation
    const params = new URLSearchParams({
      planet1,
      planet2,
      degrees: angle,
      fromDate: toYMD(barData.dates[0]),
      toDate: toYMD(barData.dates[barData.dates.length - 1]),
      // Sent explicitly in both frames rather than only for heliocentric, so the request always
      // states which frame it wants instead of relying on the endpoint's default.
      frame: frameOfReference === 'Heliocentric' ? 'heliocentric' : 'geocentric',
    });
    console.log('[LongitudeStudyDlg] Calling /api/followstudy/get-degree-separation with params:', params.toString());
    try {
      const res = await api.get(`/api/followstudy/get-degree-separation?${params.toString()}`);
      const dates = res.data;
      console.log('[LongitudeStudyDlg] Parsed dates:', dates);

      // The statistics summary, logged to the Journal tab.
      //
      // Derived here rather than requested from the backend: the endpoint returns dates only, and
      // every bar the figures are computed from is already loaded in the browser. Failing to build
      // the report must not cost the customer their overlays, so it is guarded separately from the
      // drawing below.
      if (typeof onJournalAppend === 'function') {
        try {
          const { report } = buildLongitudeStudyReport({
            planet1,
            planet2,
            distance: angle,
            frame: frameOfReference,
            eventDates: dates,
            barData,
          });
          onJournalAppend({
            studyLabel: `${planet1} - ${angle}° - ${planet2} (${frameOfReference})`,
            reportText: report,
          });
        } catch (reportErr) {
          console.error('[LongitudeStudyDlg] Could not build the journal report:', reportErr);
        }
      }

      // Pass overlays up via onRun or handle locally
      if (onRun) {
        // Construct a valid study object for App.js
        const overlays = displayType === 'verticalLine'
          ? dates.map(date => ({
              type: 'verticalLine',
              planet1,
              planet2,
              degrees: angle,
              displayType,
              color,
              thickness: 2, // match overlays
              frameOfReference: frameOfReference || '',
              // Honoured by chartDataProcessor's markLine builder as lineStyle.type.
              lineType,
              label: `${planet1}-${angle}-${planet2} (${frameAbbrev(frameOfReference)})\n${date.split('T')[0]}`,
              date: date.split('T')[0]
            }))
          // Dots and arrows anchor to the bar, so they need its high/low. One scatter overlay
          // carries every event rather than one per date: a hundred single-point series would
          // bloat the option object and make the legend meaningless.
          : [buildLongitudeMarkers(dates, barData, {
              displayType,
              color,
              label: `${planet1}-${angle}-${planet2} (${frameAbbrev(frameOfReference)})`,
            })].filter(Boolean);
        onRun(overlays);
      }
    } catch (err) {
      alert(err.message);
    }
    // Back to geocentric for the next study. The frame lives in this dialog's state, so without
    // this it persisted across runs and a later study silently inherited heliocentric from an
    // earlier one — the leak reported, though it is dialog state rather than anything global:
    // the backend takes the frame per request and holds none between them.
    setFrameOfReference('Geocentric');
    onClose();
  };

  /**
   * The TOTAL mode's run: where does (L1 + L2) mod 360 reach the target?
   *
   * <p>A separate function rather than a branch inside {@link handleRun}. The Distance path is
   * relied on by every existing run and its payload must not move; the cheapest guarantee of that
   * is not to touch it. The two share the overlay-building shape below, which is deliberate — the
   * chart draws a Total event exactly as it draws a Distance one.
   */
  const handleRunTotal = async () => {
    const target = parseFloat(total);

    await logActivity(api, user, 'RUN_STUDY', {
      studyType: 'longitudinalTotal',
      parameters: { planet1, planet2, target, frameOfReference },
    });

    if (!planet1 || !planet2 || isNaN(target)) {
      alert('Please select both planets and enter a valid total.');
      return;
    }
    if (planet1 === planet2) {
      alert('Planets cannot be identical for this study type.');
      return;
    }
    if (!barData || !Array.isArray(barData.dates) || barData.dates.length === 0) {
      console.error('[LongitudeStudyDlg] Chart data check failed:', barData);
      return;
    }

    const params = new URLSearchParams({
      planet1,
      planet2,
      target,
      fromDate: toYMD(barData.dates[0]),
      toDate: toYMD(barData.dates[barData.dates.length - 1]),
      // The same wire value the Distance study sends. It is immaterial there and decisive here:
      // the backend resolves "geocentric" to the SIDEREAL frame, which is the Vedic reading a Yog
      // boundary is defined in.
      frame: frameOfReference === 'Heliocentric' ? 'heliocentric' : 'geocentric',
    });

    try {
      const res = await api.get(`/api/followstudy/get-longitude-sum?${params.toString()}`);
      // The endpoint answers with objects, not bare dates: a sum carries its own value and whether
      // the target was crossed or merely approached, neither of which is inferable from a date.
      const events = Array.isArray(res.data) ? res.data : [];
      const dates = events.map((e) => e.date).filter(Boolean);

      if (typeof onJournalAppend === 'function') {
        try {
          const { report } = buildLongitudeStudyReport({
            planet1,
            planet2,
            distance: target,
            frame: frameOfReference,
            eventDates: dates,
            barData,
          });
          onJournalAppend({
            studyLabel: `${planet1} + ${planet2} = ${target}° (${frameOfReference}, total)`,
            reportText: report,
          });
        } catch (reportErr) {
          console.error('[LongitudeStudyDlg] Could not build the journal report:', reportErr);
        }
      }

      if (onRun) {
        const seriesLabel = `${planet1}+${planet2}=${target}° (${frameAbbrev(frameOfReference)})`;
        const overlays = displayType === 'verticalLine'
          ? events.map((e) => ({
              type: 'verticalLine',
              planet1,
              planet2,
              degrees: target,
              displayType,
              color,
              thickness: 2,
              frameOfReference: frameOfReference || '',
              lineType,
              // The sum is shown to two places and the row says whether it CROSSED the target or
              // only came near it. A daily bar can only ever be the closest sample to an instant
              // between two of them, and a label that hid that would overstate the precision.
              label: `${seriesLabel}\n${String(e.date).split('T')[0]}`
                + (typeof e.sum === 'number' ? `\nsum ${e.sum.toFixed(2)}°` : '')
                + (e.crossed ? '' : ' (nearest)'),
              date: String(e.date).split('T')[0],
            }))
          : [buildLongitudeMarkers(dates, barData, {
              displayType,
              color,
              label: seriesLabel,
            })].filter(Boolean);
        onRun(overlays);
      }
    } catch (err) {
      alert(err.message);
    }
    setFrameOfReference('Geocentric');
    onClose();
  };

  /**
   * The total field, which accepts decimals — unlike Distance, which is whole degrees.
   *
   * <p>Clamping on every keystroke the way the Distance field does makes "13.333" impossible to
   * type: "13." parses to 13 and the point is eaten before the next digit arrives. So the input is
   * held as the typed STRING and only validated in shape, with an out-of-range value refused
   * rather than silently rewritten into something the user did not ask for.
   */
  const handleTotalChange = (event) => {
    const value = event.target.value;
    if (value !== '' && !/^\d{0,3}(\.\d*)?$/.test(value)) {
      return;
    }
    const numeric = parseFloat(value);
    if (!Number.isNaN(numeric) && numeric >= 360) {
      return;
    }
    setTotal(value);
  };

  const handleDistanceChange = (event) => {
    let value = event.target.value;
    if (value === '') {
      setDistance('');
      return;
    }
    let numValue = parseInt(value, 10);
    if (isNaN(numValue)) numValue = 0;
    if (numValue > 359) numValue = 359;
    if (numValue < 0) numValue = 0;
    setDistance(numValue.toString());
  };

  const currentPlanetList = frameOfReference === 'Heliocentric'
    ? planets.filter(p => !NO_HELIOCENTRIC_POSITION.includes(p))
    : planets;

  const renderLongitudinalDistanceFields = () => (
    <>
      <Box display="flex" flexDirection="row" justifyContent="space-between" width="100%" gap={2}>
        <FormControl fullWidth size="small">
          <Typography sx={{ fontWeight: 500, fontSize: '0.9rem', mb: 0.5 }}>Planet 1</Typography>
          <Select value={planet1} onChange={(e) => setPlanet1(e.target.value)} error={planetsAreIdentical}>
            {currentPlanetList.map((p) => <MenuItem key={p} value={p}>{p}</MenuItem>)}
          </Select>
        </FormControl>
        <FormControl fullWidth size="small">
          <Typography sx={{ fontWeight: 500, fontSize: '0.9rem', mb: 0.5 }}>Planet 2</Typography>
          <Select value={planet2} onChange={(e) => setPlanet2(e.target.value)} error={planetsAreIdentical}>
            {currentPlanetList.map((p) => <MenuItem key={p} value={p}>{p}</MenuItem>)}
          </Select>
        </FormControl>
      </Box>
      {/* Two choices, stacked, each owning the number beneath it.
          RadioGroup is a column by default, so the stacking is not what needed fixing — the two
          blocks did, because four evenly spaced controls in a line read as one list of four
          rather than as a choice between two. Each option is now its own Box: the radio, its
          field indented under it, and clear air between the blocks. */}
      <FormControl component="fieldset" fullWidth sx={{ mt: 0.5 }}>
        <RadioGroup value={mode} onChange={(e) => setMode(e.target.value)}>
          {MODE_OPTIONS.map((option, index) => {
            const active = mode === option.value;
            return (
              <Box
                key={option.value}
                // The gap belongs BETWEEN the blocks, so the last one does not add trailing space
                // the dialog's own column gap has already provided.
                sx={{ mb: index < MODE_OPTIONS.length - 1 ? 2.5 : 0 }}
              >
                <FormControlLabel
                  value={option.value}
                  control={<Radio size="small" />}
                  label={(
                    <Typography sx={{ fontWeight: 500, fontSize: '0.9rem' }}>
                      {option.label}
                    </Typography>
                  )}
                  sx={{ mb: 0.25 }}
                />
                {/* Indented to the radio's label, so the field reads as belonging to the option
                    above it rather than to the group. */}
                <Box sx={{ pl: 3.75 }}>
                  <TextField
                    size="small"
                    value={option.value === MODE_DISTANCE ? distance : total}
                    onChange={option.value === MODE_DISTANCE ? handleDistanceChange : handleTotalChange}
                    // Disabled rather than hidden. Hiding makes the dialog change height as the
                    // radio moves; leaving it enabled invites typing into a box nothing will read.
                    disabled={!active}
                    inputProps={{ 'aria-label': option.label, inputMode: option.inputMode }}
                    // Always rendered, blank when inactive, so switching option does not shift
                    // everything below it. Rendering it conditionally is not enough on its own:
                    // both hints wrap to two lines at this width while a blank one occupies one,
                    // so the height is reserved rather than left to the text.
                    helperText={active ? option.hint : ' '}
                    FormHelperTextProps={{ sx: { minHeight: '2.6em', mt: 0.5 } }}
                    sx={{ width: '100%', maxWidth: 280 }}
                  />
                </Box>
              </Box>
            );
          })}
        </RadioGroup>
      </FormControl>
    </>
  );

  return (
    <>
      <Dialog open={open} onClose={onClose} PaperProps={{ style: { width: '560px', borderRadius: '12px' } }}>
        <DialogTitle sx={{ textAlign: 'center', fontWeight: 'bold', fontSize: '1.5rem', borderBottom: '1px solid #eee', pb: 1.5, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          Longitude Study
          <HelpIcon helpKey="longitudeStudy" />
        </DialogTitle>
        <DialogContent sx={{ p: 3 }}>
          <Box display="flex" flexDirection="column" gap={2.5} width="100%">
            {renderLongitudinalDistanceFields()}

          {/* Common Fields */}
          <Box display="flex" flexDirection="row" justifyContent="space-between" alignItems="center" width="100%" gap={2}>
            <Box display="flex" flexDirection="column" width="50%">
              <Typography sx={{ fontWeight: 500, fontSize: '0.9rem', mb: 0.5 }}>Display type</Typography>
              <Select value={displayType} onChange={e => setDisplayType(e.target.value)} size="small" fullWidth>
                {DISPLAY_TYPES.map(d => <MenuItem key={d.value} value={d.value}>{d.label}</MenuItem>)}
              </Select>
            </Box>
            <FormControl fullWidth size="small" style={{width: '50%'}}>
              <Typography sx={{ fontWeight: 500, fontSize: '0.9rem', mb: 0.5 }}>Color</Typography>
              <Select value={color} onChange={(e) => setColor(e.target.value)} renderValue={(selected) => (
  <Box display="flex" alignItems="center">
    <span style={{
      display: 'inline-block',
      width: 18,
      height: 18,
      borderRadius: '50%',
      backgroundColor: selected.toLowerCase(),
      marginRight: 8,
      border: '1px solid #bbb'
    }} />
    {selected}
  </Box>
)}>
  {COLORS.map((c) => (
    <MenuItem key={c} value={c}>
      <Box display="flex" alignItems="center">
        <span style={{
          display: 'inline-block',
          width: 18,
          height: 18,
          borderRadius: '50%',
          backgroundColor: c.toLowerCase(),
          marginRight: 8,
          border: '1px solid #bbb'
        }} />
        {c}
      </Box>
    </MenuItem>
  ))}
</Select>
            </FormControl>
          </Box>
          
          {/* Only the vertical line has a line to style. Disabled rather than hidden for the
              marker types, so the control does not appear and vanish as the display type changes —
              and with a reason on the tooltip rather than an unexplained greyed-out box. */}
          <Box display="flex" flexDirection="column" width="50%">
            <Typography sx={{ fontWeight: 500, fontSize: '0.9rem', mb: 0.5 }}>Line style</Typography>
            <Select
              value={lineType}
              onChange={e => setLineType(e.target.value)}
              size="small"
              fullWidth
              disabled={displayType !== 'verticalLine'}
              title={displayType === 'verticalLine' ? '' : 'Dots and arrows have no line to style'}
              inputProps={{ 'aria-label': 'Line style' }}
            >
              {LINE_STYLES.map(l => <MenuItem key={l.value} value={l.value}>{l.label}</MenuItem>)}
            </Select>
          </Box>

          <FormControl component="fieldset">
            <Typography sx={{ fontWeight: 500, fontSize: '0.9rem', mb: 0.5 }}>Frame of Reference</Typography>
            <RadioGroup row value={frameOfReference} onChange={(e) => setFrameOfReference(e.target.value)}>
              <FormControlLabel value="Geocentric" control={<Radio />} label="Geocentric" />
              <FormControlLabel value="Heliocentric" control={<Radio />} label="Heliocentric" />
            </RadioGroup>
          </FormControl>
        </Box>
      </DialogContent>
      <DialogActions sx={{ p: '16px 24px', borderTop: '1px solid #eee', justifyContent: 'space-between' }}>
        <Button onClick={onClose} color="secondary" variant="outlined">CLOSE</Button>
        <Box>
          <Button
            onClick={() => {
              if (isRunDisabled) setShowLimitModal(true);
              else if (mode === MODE_TOTAL) handleRunTotal();
              else handleRun();
            }}
            variant="contained"
            disabled={isRunDisabled || planetsAreIdentical}
          >
            Run
          </Button>
        </Box>
        {/* Modal for limit violations */}
        <Dialog open={showLimitModal} onClose={() => setShowLimitModal(false)}>
          <DialogTitle>Invalid Distance for Selected Pair</DialogTitle>
          <DialogContent>
            <Typography>{limitMessage}</Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setShowLimitModal(false)} color="primary">OK</Button>
          </DialogActions>
        </Dialog>
      </DialogActions>
    </Dialog>

    </>
  );
}

LongitudeStudyDlg.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onRun: PropTypes.func.isRequired,
  /** Command-bus prefill. Applied once per distinct object; see PREFILL_FIELDS. */
  initialConfig: PropTypes.object,
  barData: PropTypes.shape({
    symbol: PropTypes.string,
    dates: PropTypes.arrayOf(PropTypes.string),
    ohlc: PropTypes.arrayOf(
      PropTypes.arrayOf(PropTypes.number)
    ),
    volumes: PropTypes.arrayOf(PropTypes.number),
  }),
  onAddToCSG: PropTypes.func, // Optional, but needed for CSG integration
  /** Appends the run's statistics report to the Message Panel's Journal tab. */
  onJournalAppend: PropTypes.func,
};
